class AddToCartButton extends HTMLElement {
  #isSticky = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {HTMLElement | null} */
  #sentinel = null;
  /** @type {number | undefined} */
  #removeStickyTimer = undefined;

  connectedCallback() {
    this._updateStickyBehavior();
    window.addEventListener("resize", this.#handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.#handleResize);

    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    if (this.#sentinel?.parentElement) {
      this.#sentinel.parentElement.removeChild(this.#sentinel);
      this.#sentinel = null;
    }

    clearTimeout(this.#removeStickyTimer);
    this.#removeStickyTimer = undefined;
  }

  #handleResize = () => {
    this._updateStickyBehavior();
  };

  _updateStickyBehavior() {
    const isMobile = window.innerWidth < 768;

    // Remove sticky if not mobile or data-sticky not present
    if (!this.hasAttribute("data-sticky") || !isMobile) {
      this._removeSticky();
      if (this.#observer) {
        this.#observer.disconnect();
        this.#observer = null;
      }
      if (this.#sentinel?.parentElement) {
        this.#sentinel.parentElement.removeChild(this.#sentinel);
        this.#sentinel = null;
      }
      return;
    }

    // Already initialized
    if (this.#observer) return;

    // Create sentinel after the button so it stays in the original document
    // flow position even when the button goes position:fixed (sticky-atc).
    // Observing the button itself causes a loop because fixed elements leave
    // the normal flow and re-trigger the observer.
    if (!this.#sentinel) {
      this.#sentinel = document.createElement("div");
      this.#sentinel.style.height = "1px";
      this.#sentinel.style.width = "100%";
      this.#sentinel.style.pointerEvents = "none";
      this.#sentinel.setAttribute("aria-hidden", "true");
      this.insertAdjacentElement("afterend", this.#sentinel);
    }

    // Create IntersectionObserver
    this.#observer = new IntersectionObserver(this.#handleIntersection, {
      root: null,
      threshold: 1,
    });

    this.#observer.observe(this.#sentinel);
  }

  #handleIntersection = (entries) => {
    const entry = entries[0];

    // Sentinel sits immediately after the button in the flow.
    // Once its top edge scrolls above the viewport, the button is fully gone.
    if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
      this._applySticky();
    } else {
      this._removeSticky();
    }
  };

  _applySticky() {
    if (!this.#isSticky) {
      this.classList.add("sticky-atc");
      this.#isSticky = true;
    }
  }

  _removeSticky() {
    if (this.#isSticky) {
      this.classList.remove("sticky-atc");
      this.classList.add("removing-sticky-atc");
      this.#removeStickyTimer = setTimeout(() => {
        if (this.isConnected) {
          this.classList.remove("removing-sticky-atc");
        }
        this.#removeStickyTimer = undefined;
      }, 200);
      this.#isSticky = false;
    }
  }
}

customElements.define("add-to-cart", AddToCartButton);
